import { QueryKey } from "@tanstack/react-query";
import { ComponentProps, ReactNode, useContext, useMemo } from "react";

import { TableHeaderExtrasContext } from "../../../components/TableSorted";
import {
  HeaderIconGroup,
  TableRefreshingCaption,
  TableRefreshingOverlay,
  TableScrollArea,
  TableStateSpinner,
} from "./AdminPage.styles";
import { ExportCsvButton } from "./ExportCsvButton";
import { RefreshButton } from "./RefreshButton";
import { TableExportContext } from "./TableExportContext";
import { useRefreshQueries } from "./useRefreshQueries";

interface ITableWrapperProps extends ComponentProps<typeof TableScrollArea> {
  children: ReactNode;
  /**
   * Key prefixes of the queries that feed this table — what Refresh re-runs.
   * Without them the table shows no Refresh control.
   */
  refreshKeys?: readonly QueryKey[];
  /**
   * The table's query is loading new rows (a page or filter change on a
   * server-paginated list). Shows the same "Refreshing…" treatment over the
   * rows still on screen, which `keepPreviousData` would otherwise pass off
   * as the new page.
   */
  isFetching?: boolean;
}

/**
 * The wrapper every admin `TableSorted` sits in: the density-setting scroll
 * area, plus the header icons — the page's CSV export (when its pagination
 * footer configured one) and the {@link RefreshButton} for this table's own
 * queries (`refreshKeys`) — handed to the table through
 * `TableHeaderExtrasContext` so they sit inside the header row itself, at the
 * right-hand end, adding no height.
 *
 * While the table's queries are re-run the rows stay on screen, the body dims
 * and a small "Refreshing…" pill shows, and the button is busy.
 */
export function TableWrapper({
  children,
  refreshKeys,
  isFetching = false,
  ...rest
}: ITableWrapperProps) {
  const {
    isAvailable,
    isRefreshing: isRefreshRunning,
    refresh,
  } = useRefreshQueries(refreshKeys);
  const isRefreshing = isRefreshRunning || isFetching;

  // The CSV export the page handed its pagination footer, if any (see
  // `TableExportContext`) — shown here as an icon immediately left of Refresh.
  const exportConfig = useContext(TableExportContext)?.config ?? null;

  // Memoised so the context value only changes when its inputs do —
  // otherwise every render of the wrapper would re-render the table header.
  const headerExtras = useMemo(() => {
    if (!isAvailable && !exportConfig) return null;
    return (
      <HeaderIconGroup>
        {exportConfig && (
          <ExportCsvButton exportCsv={exportConfig} disabled={isRefreshing} />
        )}
        {isAvailable && (
          <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} />
        )}
      </HeaderIconGroup>
    );
  }, [isAvailable, isRefreshing, refresh, exportConfig]);

  return (
    <TableHeaderExtrasContext.Provider value={headerExtras}>
      <TableScrollArea
        data-testid="admin-table-chrome"
        {...rest}
        refreshing={isRefreshing}
        aria-busy={isRefreshing || undefined}
      >
        {children}
        {isRefreshing && (
          <TableRefreshingOverlay data-testid="admin-table-refreshing">
            <TableRefreshingCaption>
              <TableStateSpinner aria-hidden="true" />
              Refreshing…
            </TableRefreshingCaption>
          </TableRefreshingOverlay>
        )}
      </TableScrollArea>
    </TableHeaderExtrasContext.Provider>
  );
}
